import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import dayjs from "dayjs";
import { FiUsers, FiAward, FiTrendingUp, FiClock } from "react-icons/fi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TIME_RANGES = [
  { value: 7, label: "Last 7 Days" },
  { value: 14, label: "Last 14 Days" },
  { value: 30, label: "Last 30 Days" },
];

export default function GameAnalyticsCharts({
  history = [],
  dailyStats = [],
  stats = {},
  gameName = "Game",
  themeColor = "yellow", // "yellow" | "amber" | "red" | "purple"
}) {
  const [daysRange, setDaysRange] = useState(7);
  // Toggle between "sessions" (default) and "users"
  const [metricMode, setMetricMode] = useState("sessions");

  // Compute daily sessions, daily users, and daily points from dailyStats or history records
  const {
    labels,
    dailySessions,
    dailyUsers,
    dailyPoints,
    totalRangeSessions,
    totalRangeUsers,
    totalRangePoints,
    avgDailySessions,
    avgDailyUsers,
    avgDailyPoints,
  } = useMemo(() => {
    const datesMap = {};
    const today = dayjs();

    // Initialize map for each day in selected range
    for (let i = daysRange - 1; i >= 0; i--) {
      const dateKey = today.subtract(i, "day").format("YYYY-MM-DD");
      datesMap[dateKey] = {
        label: today.subtract(i, "day").format("DD MMM"),
        users: new Set(),
        usersCount: 0,
        sessionsCount: 0,
        pointsAwarded: 0,
      };
    }

    // 1. Populate from server dailyStats if provided (full accurate database aggregation)
    if (Array.isArray(dailyStats) && dailyStats.length > 0) {
      dailyStats.forEach((row) => {
        if (!row?.date) return;
        const rowDateKey = dayjs(row.date).format("YYYY-MM-DD");
        if (datesMap[rowDateKey]) {
          datesMap[rowDateKey].usersCount += Number(row.users || row.uniqueUsers || row.activeUsers || 0);
          datesMap[rowDateKey].sessionsCount += Number(row.sessions || row.totalSessions || row.count || 0);
          datesMap[rowDateKey].pointsAwarded += Number(row.points || row.totalPoints || row.pointsAwarded || row.claimedPoints || 0);
        }
      });
    }

    // 2. Also populate from history data if dailyStats is empty or to supplement
    if (Array.isArray(history) && history.length > 0) {
      history.forEach((row) => {
        const dateVal = row.createdAt || row.startedAt || row.transactionDate || row.timestamp;
        if (!dateVal) return;
        const rowDateKey = dayjs(dateVal).format("YYYY-MM-DD");
        if (datesMap[rowDateKey]) {
          const userId = row.userId || row.user_id || row.username || row.id;
          if (userId) datesMap[rowDateKey].users.add(String(userId));
          // If server dailyStats was not present for this day, accumulate from history rows
          if (!dailyStats || dailyStats.length === 0) {
            datesMap[rowDateKey].sessionsCount += 1;
            const pts = Number(row.claimedPoints ?? row.pointsAwarded ?? row.points ?? row.targetPoints ?? 0);
            datesMap[rowDateKey].pointsAwarded += pts;
          }
        }
      });
    }

    const dateKeys = Object.keys(datesMap).sort();

    const lbls = dateKeys.map((k) => datesMap[k].label);
    const sessionsArr = dateKeys.map((k) => {
      if (datesMap[k].sessionsCount > 0) return datesMap[k].sessionsCount;
      return datesMap[k].users.size > 0 ? datesMap[k].users.size : 0;
    });
    const usersArr = dateKeys.map((k) => {
      if (datesMap[k].usersCount > 0) return datesMap[k].usersCount;
      return datesMap[k].users.size;
    });
    const pointsArr = dateKeys.map((k) => datesMap[k].pointsAwarded);

    const sumSessions = sessionsArr.reduce((a, b) => a + b, 0);
    const sumUsers = usersArr.reduce((a, b) => a + b, 0);
    const sumPoints = pointsArr.reduce((a, b) => a + b, 0);

    return {
      labels: lbls,
      dailySessions: sessionsArr,
      dailyUsers: usersArr,
      dailyPoints: pointsArr,
      totalRangeSessions: sumSessions,
      totalRangeUsers: sumUsers,
      totalRangePoints: sumPoints,
      avgDailySessions: daysRange > 0 && sumSessions > 0 ? Math.round(sumSessions / daysRange) : 0,
      avgDailyUsers: daysRange > 0 && sumUsers > 0 ? Math.round(sumUsers / daysRange) : 0,
      avgDailyPoints: daysRange > 0 && sumPoints > 0 ? Math.round(sumPoints / daysRange) : 0,
    };
  }, [history, dailyStats, daysRange]);

  // Color Theme Configuration
  const themeStyles = useMemo(() => {
    switch (themeColor) {
      case "amber":
        return {
          userBarBg: "rgba(245, 158, 11, 0.2)",
          userBarBorder: "#F59E0B",
          pointLineBg: "rgba(180, 83, 9, 0.12)",
          pointLineBorder: "#B45309",
          badgeBg: "bg-amber-100 text-amber-900 border-amber-300",
          activePill: "bg-amber-800 text-white",
        };
      case "red":
        return {
          userBarBg: "rgba(239, 68, 68, 0.2)",
          userBarBorder: "#EF4444",
          pointLineBg: "rgba(185, 28, 28, 0.12)",
          pointLineBorder: "#B91C1C",
          badgeBg: "bg-red-100 text-red-900 border-red-300",
          activePill: "bg-red-700 text-white",
        };
      case "purple":
        return {
          userBarBg: "rgba(147, 51, 234, 0.2)",
          userBarBorder: "#9333EA",
          pointLineBg: "rgba(109, 40, 217, 0.12)",
          pointLineBorder: "#6D28D9",
          badgeBg: "bg-purple-100 text-purple-900 border-purple-300",
          activePill: "bg-purple-700 text-white",
        };
      case "yellow":
      default:
        return {
          userBarBg: "rgba(37, 99, 235, 0.2)",
          userBarBorder: "#2563EB",
          pointLineBg: "rgba(202, 138, 4, 0.15)",
          pointLineBorder: "#CA8A04",
          badgeBg: "bg-[#ffffcc] text-black border-yellow-300",
          activePill: "bg-[#ffff00] text-black",
        };
    }
  }, [themeColor]);

  const isSessions = metricMode === "sessions";

  // Chart 1: Daily Sessions OR Daily Users Chart Config
  const chart1Data = {
    labels,
    datasets: [
      {
        label: isSessions ? "Game Sessions" : "Active Players",
        data: isSessions ? dailySessions : dailyUsers,
        backgroundColor: themeStyles.userBarBg,
        borderColor: themeStyles.userBarBorder,
        borderWidth: 2,
        borderRadius: 8,
        hoverBackgroundColor: themeStyles.userBarBorder,
      },
    ],
  };

  // Chart 2: Daily Points Chart Config
  const pointsChartData = {
    labels,
    datasets: [
      {
        label: "Points Awarded (PTS)",
        data: dailyPoints,
        backgroundColor: themeStyles.pointLineBg,
        borderColor: themeStyles.pointLineBorder,
        borderWidth: 2.5,
        fill: true,
        tension: 0.35,
        pointBackgroundColor: themeStyles.pointLineBorder,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        titleFont: { size: 12, weight: "bold" },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: "#6B7280" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(229, 231, 235, 0.6)" },
        ticks: { font: { size: 11 }, color: "#6B7280", precision: 0 },
      },
    },
  };

  const chart1Options = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        ...chartOptions.plugins.tooltip,
        callbacks: {
          label: (context) => {
            const idx = context.dataIndex;
            const s = dailySessions[idx] || 0;
            const u = dailyUsers[idx] || 0;
            return [
              `🎮 Total Sessions: ${s.toLocaleString()}`,
              `👥 Unique Players: ${u.toLocaleString()}`,
            ];
          },
        },
      },
    },
  };

  return (
    <div className="space-y-4">
      {/* Top Header & Range Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-gray-200/80 shadow-2xs">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700">
            <FiTrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-black text-gray-900 flex items-center gap-2">
              <span>{gameName} Performance Analytics</span>
              <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full uppercase">
                Daily Trends
              </span>
            </h2>
            <p className="text-[11px] text-gray-500">
              Track daily player engagement and reward points distribution over time.
            </p>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-xl">
          {TIME_RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setDaysRange(r.value)}
              className={`px-3 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${daysRange === r.value
                  ? `${themeStyles.activePill} shadow-2xs`
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Two Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* CHART 1: DAILY SESSIONS / ACTIVE PLAYERS */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-xs">
                  {isSessions ? <FiClock size={14} /> : <FiUsers size={14} />}
                </div>
                <h3 className="text-xs font-bold text-gray-800">
                  {isSessions ? "Daily Game Sessions" : "Daily Active Players (Unique Users)"}
                </h3>
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5 ml-9">
                {isSessions
                  ? "Total completed slot rounds per day"
                  : "Unique players who played per day"}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Metric Mode Toggle: Sessions vs Unique Players */}
              <div className="flex items-center bg-gray-100 p-0.5 rounded-lg text-[11px] font-bold">
                <button
                  type="button"
                  onClick={() => setMetricMode("sessions")}
                  className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                    isSessions
                      ? "bg-white text-gray-900 shadow-2xs font-bold"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                  title="Show daily total sessions"
                >
                  Sessions
                </button>
                <button
                  type="button"
                  onClick={() => setMetricMode("users")}
                  className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                    !isSessions
                      ? "bg-white text-gray-900 shadow-2xs font-bold"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                  title="Show daily unique players"
                >
                  Unique Players
                </button>
              </div>

              <div className="text-right font-mono min-w-[70px]">
                <span className="text-lg font-black text-gray-900 block">
                  {isSessions ? totalRangeSessions.toLocaleString() : totalRangeUsers.toLocaleString()}
                </span>
                <span className="text-[10px] text-gray-400 block font-sans">
                  Avg: {isSessions ? avgDailySessions : avgDailyUsers}/day
                </span>
              </div>
            </div>
          </div>

          <div className="h-56 relative w-full pt-2">
            <Bar data={chart1Data} options={chart1Options} />
          </div>
        </div>

        {/* CHART 2: DAILY POINTS AWARDED */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-xs space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center text-xs">
                  <FiAward size={14} />
                </div>
                <h3 className="text-xs font-bold text-gray-800">
                  Daily Points Awarded
                </h3>
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5 ml-9">
                Total Zeta Event Points claimed per day
              </p>
            </div>

            <div className="text-right font-mono">
              <span className="text-lg font-black text-purple-700">
                {totalRangePoints.toLocaleString()} <span className="text-xs font-sans text-gray-500 font-bold">PTS</span>
              </span>
              <span className="text-[10px] text-gray-400 block font-sans">
                Avg: {avgDailyPoints.toLocaleString()} pts/day
              </span>
            </div>
          </div>

          <div className="h-56 relative w-full pt-2">
            <Line data={pointsChartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
}
